import React, { useState } from 'react';
import { Table, Button, Dropdown, Menu, Tag, Modal, Space, Select, Input, Tooltip } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExceptionOutlined
} from '@ant-design/icons';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import MainCard from '@/components/MainCard';

const { Option } = Select;
const { Search } = Input;

// Define ticket status types
const TicketStatus = ['pending', 'in-review', 'approved', 'rejected'];

// Ticket interface
function Ticket(key, ticketId, batchNumber, submittedBy, status, reviewMethod, details) {
    this.key = key;
    this.ticketId = ticketId;
    this.batchNumber = batchNumber;
    this.submittedBy = submittedBy;
    this.status = status;
    this.reviewMethod = reviewMethod;
    this.details = details;
}

// Commented out - external CDN font URL is broken
// const MyIcon = createFromIconfontCN({
//     scriptUrl: '//at.alicdn.com/t/font_8d5l8fzk5b87iudi.js',
// });

const ReviewException = () => {
    // Static ticket data
    const [tickets, setTickets] = useState([
        new Ticket('1', 'BATCH-2024-001', 'B2024-0001', 'John Doe', 'pending', 'Standard Review', 'Batch processing request for financial records'),
        new Ticket('2', 'BATCH-2024-002', 'B2024-0002', 'Jane Smith', 'pending', 'Detailed Review', 'Complex data reconciliation batch'),
        new Ticket('3', 'BATCH-2024-003', 'B2024-0003', 'Mike Johnson', 'pending', 'Quick Review', 'Standard operational batch processing')
    ]);

    // State for selected tickets and review modal
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
    const [currentReviewMethod, setCurrentReviewMethod] = useState('Standard Review');
    const [searchValue, setSearchValue] = useState('');

    // Ticket status color mapping
    const statusColorMap = {
        'pending': 'orange',
        'in-review': 'blue',
        'approved': 'green',
        'rejected': 'red'
    };

    // Review methods dropdown menu
    const reviewMethodsMenu = (ticketKey) => (
        <Menu>
            <Menu.Item key="standard" onClick={() => handleReviewTicket(ticketKey, 'Standard Review')}>
                Standard Review
            </Menu.Item>
            <Menu.Item key="detailed" onClick={() => handleReviewTicket(ticketKey, 'Detailed Review')}>
                Detailed Review
            </Menu.Item>
            <Menu.Item key="quick" onClick={() => handleReviewTicket(ticketKey, 'Quick Review')}>
                Quick Review
            </Menu.Item>
        </Menu>
    );

    // Handle ticket review method selection
    const handleReviewTicket = (ticketKey, method) => {
        const updatedTickets = tickets.map(ticket =>
            ticket.key === ticketKey
                ? { ...ticket, status: 'in-review', reviewMethod: method }
                : ticket
        );
        setTickets(updatedTickets);
    };

    // Batch review handler
    const handleBatchReview = () => {
        setIsReviewModalVisible(true);
    };

    // Confirm batch review
    const confirmBatchReview = () => {
        const updatedTickets = tickets.map(ticket =>
            selectedTickets.includes(ticket.key)
                ? {
                    ...ticket,
                    status: currentReviewMethod.includes('Detailed') ? 'approved' : 'in-review'
                }
                : ticket
        );

        setTickets(updatedTickets);
        setIsReviewModalVisible(false);
        setSelectedTickets([]);
    };

    // Table columns configuration
    const allColumns = [
        {
            title: 'Ticket ID',
            dataIndex: 'ticketId',
            key: 'ticketId',
            defaultVisible: true,
            sortable: true,
            sortType: 'string',
            width: 150,
        },
        {
            title: 'Batch Number',
            dataIndex: 'batchNumber',
            key: 'batchNumber',
            defaultVisible: true,
            sortable: true,
            sortType: 'string',
            width: 150,
        },
        {
            title: 'Submitted By',
            dataIndex: 'submittedBy',
            key: 'submittedBy',
            defaultVisible: true,
            sortable: true,
            sortType: 'string',
            width: 150,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            defaultVisible: true,
            sortable: true,
            sortType: 'string',
            width: 120,
            render: (status) => (
                <Tag color={statusColorMap[status]}>
                    {status.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Review Method',
            dataIndex: 'reviewMethod',
            key: 'reviewMethod',
            defaultVisible: true,
            sortable: true,
            sortType: 'string',
            width: 180,
            render: (text, record) => (
                <Dropdown overlay={() => reviewMethodsMenu(record.key)}>
                    <Button>
                        {text}
                    </Button>
                </Dropdown>
            )
        },
        {
            title: 'Details',
            dataIndex: 'details',
            key: 'details',
            defaultVisible: true,
            sortable: false,
        }
    ];

    const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
        tableKey: 'reviewException_tickets',
        columns: allColumns,
        data: tickets,
        searchValue: searchValue,
    });

    return (
        <div>
            <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Button
                    type="primary"
                    onClick={handleBatchReview}
                    disabled={selectedTickets.length === 0}
                >
                    Batch Review
                </Button>
                <Space>
                    <Search
                        placeholder="Search exceptions..."
                        allowClear
                        style={{ width: 300 }}
                        onChange={(e) => setSearchValue(e.target.value)}
                        value={searchValue}
                    />
                    <TableColumnConfig menu={columnConfigMenu} className="mb-3" />
                </Space>
            </Space>
           <MainCard>
            <Table
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selectedTickets,
                    onChange: (selectedRowKeys) => setSelectedTickets(selectedRowKeys)
                }}
                columns={visibleColumns}
                dataSource={filteredData}
                size="small"
                bordered
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
                scroll={{ x: 'max-content' }}
            />
            </MainCard>

            <Modal
                title="Batch Review Confirmation"
                open={isReviewModalVisible}
                onOk={confirmBatchReview}
                onCancel={() => setIsReviewModalVisible(false)}
            >
                <p>Selected Tickets: {selectedTickets.length}</p>
                <Select
                    style={{ width: '100%', marginBottom: 16 }}
                    placeholder="Select Review Method"
                    value={currentReviewMethod}
                    onChange={(value) => setCurrentReviewMethod(value)}
                >
                    <Option value="Standard Review">Standard Review</Option>
                    <Option value="Detailed Review">Detailed Review</Option>
                    <Option value="Quick Review">Quick Review</Option>
                </Select>
            </Modal>
        </div>
    );
};

export default ReviewException;